import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import api from "../lib/api";
import { StatusBadge, PriorityBadge, ClientRepliedBadge } from "./Badges";
import { SearchableSelect } from "./ui/searchable-select";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import useFieldConfigs from "../hooks/useFieldConfigs";
import { useAuth } from "../context/AuthContext";
import { useStatuses } from "../context/StatusContext";
import { PRIORITIES, formatDate, initials, subtypesText } from "../lib/utils";

const DONE_BUCKETS = new Set(["completed", "rejected"]);
const ACTIVE_BUCKETS = new Set(["open", "in_progress", "pending_client", "none"]);

// Fetches its own paginated tickets scoped by `scopeParams` (e.g. {project_id}
// or {organization_id}) — mirrors the Active/Completed/All tab pattern from
// the main Tickets page so large clients/projects don't load every ticket at once.
export default function TicketsPanel({ scopeParams, testId = "tickets-panel" }) {
  const { user } = useAuth();
  const isStaff = !["client_admin", "client_user"].includes(user.role);
  const { fieldOn } = useFieldConfigs();
  const { list: statusList } = useStatuses();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState({ active: true, completed: false, all: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const scopeKey = JSON.stringify(scopeParams || {});

  const buildParams = (pageNum) => {
    const params = { ...(scopeParams || {}), page: pageNum, page_size: 50 };
    if (tab !== "all") params.view = tab;
    if (status !== "all") params.status = status;
    if (priority !== "all") params.priority = priority;
    if (search.trim()) params.search = search.trim();
    return params;
  };

  const requestIdRef = useRef(0);
  const fetchPage = (pageNum, append = false) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    api.get("/tickets", { params: buildParams(pageNum) })
      .then((r) => {
        if (reqId !== requestIdRef.current) return;
        setTotal(r.data.total);
        setPage(r.data.page);
        setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        if (!append) { setItems([]); setTotal(0); }
        setError(true);
      })
      .finally(() => { if (reqId === requestIdRef.current) setLoading(false); });
  };

  // Reset everything when the scope (project/org) changes.
  useEffect(() => {
    requestIdRef.current++;
    setTab("active");
    setStatus("all"); setPriority("all"); setSearch("");
    setItems([]); setTotal(0);
    setLoaded({ active: true, completed: false, all: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const prevTabRef = useRef(tab);
  const debounceRef = useRef(null);
  useEffect(() => {
    const tabChanged = prevTabRef.current !== tab;
    prevTabRef.current = tab;
    requestIdRef.current++;
    if (tab !== "active" && !loaded[tab] && tabChanged) {
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      if (tab !== "active" && !loaded[tab]) setLoaded((l) => ({ ...l, [tab]: true }));
      fetchPage(1);
    }, 250);
    return () => {
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, tab, status, priority, search, loaded[tab]]);

  const statusOptions = (statusList || []).filter((s) => s.enabled && (!isStaff || !s.hide_from_staff));
  const showPriority = fieldOn("priority");
  const cols = 5 + (showPriority ? 1 : 0);
  const showTable = tab === "active" || loaded[tab];
  const hasMore = showTable && items.length < total;

  return (
    <div className="surface-card overflow-hidden" data-testid={testId}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Work</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
            Tickets{showTable && !loading ? ` (${total})` : ""}
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 border border-border rounded-md px-3 h-9 bg-white min-w-[180px]">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400 w-28"
              data-testid={`${testId}-search`} />
          </div>
          <SearchableSelect value={status} onValueChange={setStatus}
            options={[{ value: "all", label: "All statuses" }, ...statusOptions.map((s) => ({ value: s.key, label: s.label }))]}
            placeholder="Status" className="w-[160px]" testId={`${testId}-status`} />
          {showPriority && (
            <SearchableSelect value={priority} onValueChange={setPriority}
              options={[{ value: "all", label: "Any priority" }, ...PRIORITIES.map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))]}
              placeholder="Priority" className="w-[140px]" testId={`${testId}-priority`} />
          )}
        </div>
      </div>

      <div className="px-5 pt-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid={`${testId}-tabs`}>
            <TabsTrigger value="active" data-testid={`${testId}-tab-active`}>Active</TabsTrigger>
            <TabsTrigger value="completed" data-testid={`${testId}-tab-completed`}>Completed</TabsTrigger>
            <TabsTrigger value="all" data-testid={`${testId}-tab-all`}>All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!showTable ? (
        <div className="px-5 py-10 text-center">
          <p className="text-sm text-slate-500 mb-3">
            {tab === "completed" ? "Load completed tickets for this view." : "Load every ticket for this view."}
          </p>
          <Button onClick={() => {
            if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
            setLoaded((l) => ({ ...l, [tab]: true }));
            fetchPage(1);
          }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`${testId}-load-button`}>
            Load tickets
          </Button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-tight">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
                  <th>Ticket</th><th>Subject</th><th>Status</th>{showPriority && <th>Priority</th>}
                  <th className="hidden md:table-cell">Assignee</th><th>Created</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={cols} className="text-center text-slate-400 py-10 text-xs">Loading tickets…</td></tr>}
                {!loading && error && (
                  <tr><td colSpan={cols} className="text-center text-rose-500 py-10 text-xs">Couldn't load tickets — refresh to try again.</td></tr>
                )}
                {!loading && !error && items.length === 0 && (
                  <tr><td colSpan={cols} className="text-center text-slate-400 py-10 text-xs">No tickets match.</td></tr>
                )}
                {!loading && items.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                    <td><Link to={`/tickets/${t.id}`} className="font-mono text-blue-600 hover:underline">{t.ticket_no}</Link></td>
                    <td className="max-w-md">
                      <Link to={`/tickets/${t.id}`} className="block font-medium text-slate-800 truncate hover:text-blue-600">{t.subject}</Link>
                      {isStaff && <ClientRepliedBadge at={t.client_replied_at} className="mt-0.5" />}
                      {fieldOn("request_type") && t.request_type && (
                        <div className="text-xs text-slate-400 truncate">{t.request_type.replace(/_/g, " ")}{subtypesText(t) ? ` · ${subtypesText(t)}` : ""}</div>
                      )}
                    </td>
                    <td><StatusBadge status={t.status} /></td>
                    {showPriority && <td><PriorityBadge priority={t.priority} /></td>}
                    <td className="hidden md:table-cell">
                      {t.assignee ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-semibold">{initials(t.assignee.name)}</div>
                          <span className="text-xs text-slate-700">{t.assignee.name}</span>
                        </div>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="text-slate-500 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="px-5 py-3 border-t border-slate-100 text-center">
              <Button variant="outline" className="border-slate-300" disabled={loading}
                onClick={() => fetchPage(page + 1, true)} data-testid={`${testId}-load-more`}>
                {loading ? "Loading…" : `Load more (${items.length} of ${total})`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
